/**
 * Sawyer B1 — a bed chosen WHILE booking (Quick Book) or while editing a stay
 * (Edit reservation). Pure: no React, no I/O.
 *
 * Sawyer, 2026-10-23: "when I'm booking, he's trying to select a bed over
 * nights … there's no beds assigned yet … I tried this and I couldn't get it
 * to work." The Lodging calendar already places an unassigned boarder; this
 * module lets the booking surfaces make the SAME decision with the SAME
 * verdict (`bedPickerState` in lodging-moves.ts, unchanged), against the same
 * lodging read, and plan the SAME writes (POST / PATCH / DELETE
 * /calendar/assign). Nothing here decides a bed the server would refuse; the
 * server stays the authority and its sentence is shown when it refuses.
 */
import type { LodgingCalendarView, LodgingUnit } from './api.js';
import { bedPickerState, type Placement } from './lodging-moves.js';
import { rolesHavePermission } from './rbac-matrix.js';

/** The lodging route's cap: start..end inclusive, at most 92 days. */
export const LODGING_READ_MAX_DAYS = 92;

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * The placement a booking being made (or edited) would ask for. Booking end
 * dates are inclusive and the bed row's end is the checkout day — the same
 * VALUE (lodging-moves.ts `placementOfBoarder`). null when there is no night
 * (pick-up on or before drop-off) or no dog. `customerId` may be null: the
 * verdict then treats every shared bed's occupant as another family (never
 * suggests a shared bed it cannot vouch for).
 */
export function draftPlacement(args: {
  customerId: string | null;
  dogCount: number;
  startDate: string;
  endDate: string;
  assignmentId?: string | null;
}): Placement | null {
  if (args.dogCount < 1) return null;
  if (!args.startDate || !args.endDate || args.endDate <= args.startDate) return null;
  return {
    assignmentId: args.assignmentId ?? null,
    // The verdict never reads it; a draft has no booking yet.
    bookingId: '',
    customerId: args.customerId,
    dogCount: args.dogCount,
    startDate: args.startDate,
    endDate: args.endDate,
  };
}

/** The read that covers `start..end`, clamped to the route's 92-day cap. */
export function bedReadRange(startDate: string, endDate: string): { start: string; end: string } {
  const lo = startDate <= endDate ? startDate : endDate;
  const hi = startDate <= endDate ? endDate : startDate;
  const cap = addDays(lo, LODGING_READ_MAX_DAYS - 1);
  return { start: lo, end: hi > cap ? cap : hi };
}

/**
 * Refuter C-5 — the edit dialog's ONE read must cover the stay's old nights
 * (to find its current bed) AND the new ones (to judge availability), even
 * when the desk moves the stay to dates that do not overlap the old ones.
 */
export function unionReadRange(
  a: { startDate: string; endDate: string } | null,
  b: { startDate: string; endDate: string } | null,
): { start: string; end: string } | null {
  const spans = [a, b].filter((s): s is { startDate: string; endDate: string } => s !== null && Boolean(s.startDate && s.endDate));
  if (spans.length === 0) return null;
  const start = spans.map((s) => s.startDate).sort()[0]!;
  const end = spans.map((s) => s.endDate).sort().reverse()[0]!;
  return bedReadRange(start, end);
}

export type BedFieldState =
  | { kind: 'need-draft' }
  | { kind: 'same-day' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'no-beds' }
  | { kind: 'none-free' }
  | { kind: 'offer'; beds: LodgingUnit[] };

/**
 * What the Bed field shows. Order matters: who is staying and for how long
 * decide whether a bed applies at all, before any read is judged.
 */
export function bedFieldState(args: {
  /** A customer and at least one dog are chosen. */
  hasParty: boolean;
  /** Pick-up is after drop-off — there is at least one night. */
  overnight: boolean;
  /** Kept for the caller's "Checking beds…" line; a re-read keeps the last data on screen. */
  loading: boolean;
  error: boolean;
  data: LodgingCalendarView | null;
  placement: Placement | null;
}): BedFieldState {
  if (!args.hasParty) return { kind: 'need-draft' };
  if (!args.overnight || !args.placement) return { kind: 'same-day' };
  if (args.error) return { kind: 'error' };
  // A re-read (dates or dogs changed) keeps judging the last answer on screen
  // — the select is disabled until the new one lands — instead of flashing
  // the list away; only the very first read shows "Checking beds…".
  if (!args.data) return { kind: 'loading' };
  const picker = bedPickerState(args.data.units, args.placement, args.data.stays);
  if (picker.kind === 'no-beds') return { kind: 'no-beds' };
  if (picker.kind === 'none-free') return { kind: 'none-free' };
  return { kind: 'offer', beds: picker.beds };
}

/** A stay's bed as the edit dialog needs it — from the lodging read, never a prop (refuter C-6). */
export interface StayBed {
  /** null = the stay is a boarder with no bed yet. */
  assignmentId: string | null;
  unitId: string | null;
  unitName: string | null;
  customerId: string | null;
  dogCount: number;
  /** The bed row's span (checkout exclusive), when it has one. */
  startDate: string | null;
  endDate: string | null;
}

export function stayBedOnRead(data: LodgingCalendarView | null, bookingId: string): StayBed | null {
  if (!data) return null;
  const stay = data.stays.find((s) => s.bookingId === bookingId);
  if (stay) {
    const unit = data.units.find((u) => u.id === stay.resourceUnitId);
    return {
      assignmentId: stay.assignmentId,
      unitId: stay.resourceUnitId,
      unitName: unit?.name ?? null,
      customerId: stay.customerId,
      dogCount: stay.dogCount,
      startDate: stay.startDate,
      endDate: stay.endDate,
    };
  }
  const boarder = data.unassigned.find((b) => b.bookingId === bookingId);
  if (boarder) {
    return {
      assignmentId: null,
      unitId: null,
      unitName: null,
      customerId: boarder.customerId,
      dogCount: boarder.dogCount,
      startDate: null,
      endDate: null,
    };
  }
  return null;
}

/** The bed select's option label — the calendar's own wording for a shared bed. */
export function bedChoiceLabel(unit: LodgingUnit): string {
  return `${unit.name}${unit.capacity > 1 ? ` (up to ${unit.capacity} dogs)` : ''}`;
}

/**
 * Who sees the Bed field: the assign write (`bookings.daycare-boarding:write:all`
 * or `bookings.grooming:write:all`, route-permission-map `/assign`) AND a
 * lodging read (the desk-trio anyOf on `/lodging`). Floor roles never see it
 * and never send the read.
 */
export function canPlaceBeds(roles: ReadonlyArray<string>): boolean {
  const write =
    rolesHavePermission(roles, 'bookings.daycare-boarding:write:all') ||
    rolesHavePermission(roles, 'bookings.grooming:write:all');
  const read = [
    'bookings.daycare-boarding:read:all',
    'bookings.grooming:read:all',
    'bookings.daycare-boarding:read:scoped',
    'bookings.grooming:read:scoped',
  ].some((p) => rolesHavePermission(roles, p));
  return write && read;
}

/**
 * Refuter C-11 — where beds are set up depends on who is asking: Settings →
 * Lodging is editable only with `business-config:write:all` today.
 */
export function noBedsCopy(canEditBeds: boolean): string {
  return canEditBeds
    ? 'No beds are set up yet. Add them in Settings → Lodging — you can still book now and place the dog later.'
    : 'No beds are set up yet. An owner adds them in Settings → Lodging — you can still book now and place the dog later.';
}

// ── The edit dialog's bed write ───────────────────────────────────────────

export type BedWrite =
  | { kind: 'none' }
  | { kind: 'assign'; unitId: string; startDate: string; endDate: string }
  | { kind: 'move'; assignmentId: string; unitId: string; startDate: string; endDate: string }
  | { kind: 'release'; assignmentId: string; reason: 'chosen' | 'not-overnight' };

/**
 * What Save sends for the bed, from the stay's current bed and the desk's
 * choice. `applies` is false when the stay is no longer an overnight boarding
 * stay (one day, or another service): a bed it still holds is released
 * (refuter C-4), never left on nights the dog is not here. A kept bed with new
 * dates is a move of the same bed to the new span (the bed follows the stay).
 */
export function planBedWrite(args: {
  current: StayBed | null;
  /** '' = no bed. */
  chosenUnitId: string;
  applies: boolean;
  span: { startDate: string; endDate: string };
}): BedWrite {
  const held = args.current?.assignmentId ?? null;
  if (!args.applies) return held ? { kind: 'release', assignmentId: held, reason: 'not-overnight' } : { kind: 'none' };
  if (args.chosenUnitId === '') return held ? { kind: 'release', assignmentId: held, reason: 'chosen' } : { kind: 'none' };
  if (!held) return { kind: 'assign', unitId: args.chosenUnitId, ...args.span };
  const same =
    args.current!.unitId === args.chosenUnitId &&
    args.current!.startDate === args.span.startDate &&
    args.current!.endDate === args.span.endDate;
  return same ? { kind: 'none' } : { kind: 'move', assignmentId: held, unitId: args.chosenUnitId, ...args.span };
}

/** The reason line read before Save — names the dog and the bed, never an id. */
export function bedWriteSentence(
  write: BedWrite,
  dog: string,
  names: { current: string | null; chosen: string | null },
): string | null {
  switch (write.kind) {
    case 'none':
      return null;
    case 'assign':
      return `Puts ${dog} on ${names.chosen ?? 'the chosen bed'}.`;
    case 'move':
      return names.current && names.current === names.chosen
        ? `${names.chosen} moves with the new dates.`
        : `Moves ${dog} to ${names.chosen ?? 'the chosen bed'}.`;
    case 'release':
      return write.reason === 'not-overnight'
        ? `Takes ${dog} off ${names.current ?? 'the bed'} — a bed is for overnight stays.`
        : `Takes ${dog} off ${names.current ?? 'the bed'}.`;
  }
}
